/**
 * Runner compensation + assignment eligibility.
 */

export const COMPENSATION_TYPES = ['percent', 'fixed'] as const;
export type CompensationType = (typeof COMPENSATION_TYPES)[number];

export type AssignmentProfile = {
  role?: string;
  status?: string;
  commission?: number | null;
  compensationType?: CompensationType | string | null;
  fixedFee?: number | null;
  /** Banker/god must enable the service agreement before assignments. */
  agreementEnabled?: boolean | null;
};

export function parseCompensationType(raw: unknown): CompensationType {
  return raw === 'fixed' ? 'fixed' : 'percent';
}

/**
 * Legacy profiles without agreementEnabled are treated as enabled when they
 * already have a commission (seeded staff). New runners default to false.
 */
export function isServiceAgreementEnabled(user: AssignmentProfile): boolean {
  if (typeof user.agreementEnabled === 'boolean') return user.agreementEnabled;
  return user.commission != null;
}

export function canReceiveRouteAssignments(user: AssignmentProfile): boolean {
  if (String(user.role ?? '') !== 'runner') return false;
  if (String(user.status ?? '') !== 'active') return false;
  if (!isServiceAgreementEnabled(user)) return false;
  const type = parseCompensationType(user.compensationType);
  if (type === 'fixed') {
    return Number(user.fixedFee ?? 0) >= 0;
  }
  return Number(user.commission ?? 0) >= 0;
}

export function explainAssignmentBlock(user: AssignmentProfile): string | null {
  if (String(user.role ?? '') !== 'runner') return 'not_runner';
  if (String(user.status ?? '') !== 'active') return 'runner_inactive';
  if (!isServiceAgreementEnabled(user)) return 'agreement_disabled';
  return null;
}
